import { Grid, SelectChangeEvent, useEventCallback } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { memo, ReactElement } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { FormTypeOperationVM } from '.';
import ListTypePractice from 'practice/components/shared/list-type-practice';
import ListTypeDocument from 'shell/components/shared/list-type-document';
import ListYesNo from 'shell/components/shared/list-yes-no';
import ListCodeMovement from './list-code-movement';

interface FormTypeOperationProps {
  disabled?: boolean;
  values?: FormTypeOperationVM;
  isEdit?: boolean;
  onChange: (
    key: keyof FormTypeOperationVM,
    value: FormTypeOperationVM[keyof FormTypeOperationVM]
  ) => void;
  errors: FormikErrors<FormTypeOperationVM> | undefined;
  touched: FormikTouched<FormTypeOperationVM> | undefined;
}

const FormTypeOperationComponent = ({
  disabled,
  values,
  onChange,
  isEdit,
  touched,
  errors,
}: FormTypeOperationProps): ReactElement => {
  const handleListTypeDocumentChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('typeDocument', event.target.value);
  });

  const handleListTypePracticeChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('practice', event.target.value);
  });

  const handleListYesNoChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('printDoc', event.target.value);
  });

  const handleListInUseChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('inUse', event.target.value);
  });

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md={4}>
        <ListTypePractice
          value={values?.practice || ''}
          onChange={handleListTypePracticeChange}
          error={touched?.practice && Boolean(errors?.practice)}
          required
          disabled={disabled || isEdit}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <PmiTextField
          aria-label="Codice tipo operazione"
          label="Codice tipo operazione *"
          id="code"
          name="code"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.code || ''}
          onChange={(event) => onChange('code', event.target.value)}
          inputProps={{
            maxLength: '2',
          }}
          disabled={disabled || isEdit}
          error={touched?.code && Boolean(errors?.code)}
          helperText={errors?.code}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <PmiTextField
          aria-label="Descrizione breve tipo operazione"
          label="Descrizione breve tipo operazione"
          id="descriptionBrief"
          name="descriptionBrief"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.descriptionBrief || ''}
          onChange={(event) => onChange('descriptionBrief', event.target.value)}
          inputProps={{
            maxLength: '100',
          }}
          disabled={disabled}
          error={touched?.descriptionBrief && Boolean(errors?.descriptionBrief)}
          helperText={errors?.descriptionBrief}
        />
      </Grid>
      <Grid item xs={12} md={12}>
        <PmiTextField
          aria-label="Descrizione tipo operazione"
          label="Descrizione tipo operazione"
          id="description"
          name="description"
          fullWidth
          type="text"
          variant="outlined"
          value={values?.description || ''}
          onChange={(event) => onChange('description', event.target.value)}
          inputProps={{
            maxLength: '250',
          }}
          disabled={disabled}
          error={touched?.description && Boolean(errors?.description)}
          helperText={errors?.description}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListTypeDocument
          value={values?.typeDocument || ''}
          onChange={handleListTypeDocumentChange}
          error={touched?.typeDocument && Boolean(errors?.typeDocument)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListYesNo
          label="Stampa documento"
          value={values?.printDoc || ''}
          onChange={handleListYesNoChange}
          error={touched?.printDoc && Boolean(errors?.printDoc)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListYesNo
          label="In uso"
          value={values?.inUse || ''}
          onChange={handleListInUseChange}
          error={touched?.inUse && Boolean(errors?.inUse)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ListCodeMovement
          value={values?.codeMovement || ''}
          onChange={(value) => onChange('codeMovement', value)}
          error={touched?.codeMovement && Boolean(errors?.codeMovement)}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

FormTypeOperationComponent.displayName = 'Form Type operation Page component';

const FormTypeOperation = memo(FormTypeOperationComponent);

export default FormTypeOperation;
